/**
 * Sibling product picker: colour/size options that are separate Shopify products.
 * See docs/superpowers/specs/2026-09-08-sibling-product-picker-design.md
 */
function resolveSibling(list, currentHandle, axis, value) {
  const other = axis === 'colour' ? 'size' : 'colour';
  const current = list.find((s) => s.handle === currentHandle);
  const candidates = list.filter((s) => s[axis] === value);
  if (candidates.length === 0) return undefined;
  if (current) {
    const match = candidates.find((s) => s[other] === current[other]);
    if (match) return match;
  }
  return candidates[0];
}

const siblingCache = new Map(); // handle -> section innerHTML, per page load
let swapSeq = 0; // guard against concurrent clicks and stale responses
// The tag is `defer`, so every original script[src] tag is already in the DOM by the time
// this module evaluates. Snapshot it once so a swap never re-executes a script that ran
// on initial load (this file included).
const loadedScripts = new Set(
  typeof document === 'undefined' ? [] : Array.from(document.querySelectorAll('script[src]'), (s) => s.src)
);

if (!customElements.get('product-sibling-picker')) {
  customElements.define(
    'product-sibling-picker',
    class ProductSiblingPicker extends HTMLElement {
      connectedCallback() {
        const json = this.querySelector('script[type="application/json"]');
        this.list = json ? JSON.parse(json.textContent) : [];
        this.sectionId = this.dataset.section;
        this.current = this.dataset.current;

        const wrapper = this.sectionEl();
        if (wrapper && !siblingCache.has(this.current)) {
          siblingCache.set(this.current, wrapper.innerHTML);
        }

        this.markSoldOut();
        this.addEventListener('click', this.onClick.bind(this));
      }

      sectionEl() {
        return document.getElementById(`shopify-section-${this.sectionId}`);
      }

      markSoldOut() {
        this.querySelectorAll('button[data-axis]').forEach((btn) => {
          const target = resolveSibling(this.list, this.current, btn.dataset.axis, btn.dataset.value);
          btn.classList.toggle('is-soldout', Boolean(target) && !target.available);
        });
      }

      onClick(event) {
        const btn = event.target.closest('button[data-axis]');
        if (!btn) return;
        const target = resolveSibling(this.list, this.current, btn.dataset.axis, btn.dataset.value);
        if (!target || target.handle === this.current) return;

        const seq = ++swapSeq;

        if (siblingCache.has(target.handle)) {
          this.swap(target, siblingCache.get(target.handle), btn.dataset.axis);
          return;
        }

        const wrapper = this.sectionEl();
        if (!wrapper) {
          window.location = target.url;
          return;
        }
        wrapper.setAttribute('aria-busy', 'true');

        fetch(`${target.url}?section_id=${this.sectionId}`)
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((text) => {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const otherJson = doc.querySelector('product-sibling-picker script[type="application/json"]');
            if (!otherJson || !this.sameGroup(JSON.parse(otherJson.textContent))) {
              if (seq === swapSeq) window.location = target.url;
              return;
            }
            const section = doc.getElementById(`shopify-section-${this.sectionId}`);
            if (!section) {
              if (seq === swapSeq) window.location = target.url;
              return;
            }
            // Cache the response even if a later click has already superseded this one, but
            // never navigate or swap for a superseded response.
            siblingCache.set(target.handle, section.innerHTML);
            if (seq !== swapSeq) return;
            this.swap(target, section.innerHTML, btn.dataset.axis);
          })
          .catch(() => {
            if (seq !== swapSeq) return;
            window.location = target.url;
          });
      }

      sameGroup(otherList) {
        const key = (list) => list.map((s) => s.handle).sort().join(',');
        return key(this.list) === key(otherList);
      }

      swap(target, html, axis) {
        const wrapper = this.sectionEl();
        if (!wrapper) {
          window.location = target.url;
          return;
        }
        wrapper.innerHTML = html;
        this.loadNewScripts(wrapper);
        const pressed = wrapper.querySelector(`product-sibling-picker button[data-axis="${axis}"][aria-pressed="true"]`);
        if (pressed) pressed.focus();
        window.history.replaceState({}, '', target.url);
        wrapper.removeAttribute('aria-busy');
      }

      // Scripts inserted via innerHTML never execute. Load any src not already executed on this page.
      loadNewScripts(root) {
        root.querySelectorAll('script[src]').forEach((inert) => {
          if (loadedScripts.has(inert.src)) return;
          loadedScripts.add(inert.src);
          const live = document.createElement('script');
          live.src = inert.src;
          live.async = false; // preserve insertion order; `defer` is ignored on dynamic scripts
          document.head.appendChild(live);
        });
      }
    }
  );
}


/**
 * Add-to-cart confirm gate. Returns true when the submit must be held back and the
 * dialog shown. A submit that follows a confirmation passes through once, then the
 * gate re-arms for the next click.
 */
function needsConfirm(state) {
  if (!state.enabled) return false;
  if (state.confirmed) {
    state.confirmed = false;
    return false;
  }
  return true;
}

/**
 * Confirm dialog on add-to-cart, plus the sizing note box under the button.
 * Listens on document in the capture phase so it runs before <product-form>'s
 * own submit handler regardless of script order.
 */
if (!customElements.get('sibling-atc-warning')) {
  customElements.define(
    'sibling-atc-warning',
    class SiblingAtcWarning extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.state = { enabled: this.dataset.confirm === 'true', confirmed: false };
        if (!this.dialog || !this.state.enabled) return;

        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        this.dialog.addEventListener('close', () => {
          if (this.dialog.returnValue !== 'confirm') return;
          this.state.confirmed = true;
          this.form().requestSubmit();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
      }

      form() {
        return document.getElementById(this.dataset.form);
      }

      onSubmit(event) {
        if (event.target !== this.form()) return;
        if (!needsConfirm(this.state)) return;
        event.preventDefault();
        event.stopPropagation();
        this.dialog.returnValue = '';
        this.dialog.showModal();
      }
    }
  );
}

if (typeof module !== 'undefined') module.exports = { resolveSibling, needsConfirm };
